import { button } from "@/components/ui";

/**
 * Mikro-Knopf: ein Tipp startet die Aufnahme, ein zweiter beendet sie
 * explizit. Endet NIE von selbst durch Stille – nur durch erneutes Tippen
 * (siehe Anforderung an die Mikro-Erfassung).
 *
 * Bewusst über den gemeinsamen `button()`-Baustein gebaut statt mit eigenem
 * Markup: dadurch sieht er automatisch aus wie der Stift daneben (gleiches
 * Quadrat, gleicher Rahmen, gleiches Hover) und bleibt es auch, wenn sich das
 * Knopf-Design irgendwann ändert. `.voice-mic-btn` fügt nur noch das
 * Pulsieren während der Aufnahme hinzu.
 */
export function micButton(opts: {
  title?: string;
  onStop: (audio: Blob) => void;
  onError?: (err: unknown) => void;
}): HTMLButtonElement {
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let stream: MediaStream | null = null;

  const btn = button({
    icon: "mic",
    variant: "ghost",
    title: opts.title ?? "Spracheingabe",
  });
  btn.classList.add("voice-mic-btn");

  function stopStream(): void {
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
  }

  async function start(): Promise<void> {
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks = [];
      recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      recorder.onstop = () => {
        const type = recorder?.mimeType || "audio/webm";
        stopStream();
        btn.classList.remove("is-recording");
        btn.title = opts.title ?? "Spracheingabe";
        if (chunks.length) opts.onStop(new Blob(chunks, { type }));
      };
      recorder.start();
      btn.classList.add("is-recording");
      btn.title = "Aufnahme beenden";
    } catch (err) {
      opts.onError?.(err);
    }
  }

  function stop(): void {
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }

  btn.addEventListener("click", () => {
    if (recorder && recorder.state === "recording") stop();
    else void start();
  });

  return btn;
}
